/**
 * SDK 移动端垂直连续切月(竖向轮播)。
 *
 * 移植自 src/ui/composites/calendar/useVerticalSwipeMonth.ts(页面 react-day-picker 版),
 * 适配 SDK 自绘日历:测量目标改为 `.hh-cal__month`(单月元素)而非 rdp 的 `.rdp-months`。
 * 逻辑与页面版一致,以保证 SDK 与 /hub/packages 切月体验对齐。
 *
 * 配合 Calendar 以三月 [上月,当月,下月] 竖向堆叠渲染,裁剪容器只露当月(中间):
 *  - 静止:整块 translateY(-monthH),当月(中间)填满裁剪区。
 *  - 拖动:整块跟手 translateY(-monthH + dy),上滑露出下月、下滑露出上月(连续轮播)。
 *  - 松手超阈值:动画平移到相邻月完全可见,结束后换月 + 瞬移归 0(无跳动);未达阈值回弹。
 *
 * 仅 touch;touchmove 用原生非 passive 监听以 preventDefault,阻止弹层/页面竖向滚动。
 * 水平手势不拦截,交还默认。桌面 enabled=false:不测量、不挂监听、translate 恒 0。
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Options {
  /** 当前显示月份 */
  month: Date;
  /** 切月回调 */
  onChange: (m: Date) => void;
  /** 加 N 月(SDK 自带,避免 date-fns) */
  addMonths: (d: Date, n: number) => Date;
  /** 是否启用手势(仅移动端) */
  enabled: boolean;
  /** 触发切月的位移阈值(px) */
  threshold?: number;
}

type Phase = 'idle' | 'dragging' | 'animating';

const DURATION = 280; // 必须与 Calendar 里 transition 时长(0.28s)一致

export function useVerticalSwipeMonth({
  month,
  onChange,
  addMonths,
  enabled,
  threshold = 50,
}: Options) {
  const clipRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const [monthH, setMonthH] = useState(0);
  const [offset, setOffset] = useState(0);
  const [transition, setTransition] = useState(false);

  const phaseRef = useRef<Phase>('idle');
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const heightRef = useRef(0); // 拖动起始时的单月高度(隔离拖动期间的状态变化)
  const lockedRef = useRef(false); // 已锁定为竖向

  // 最新值入 ref,供原生监听器读取,避免闭包陈旧(不在 render 阶段写 ref)。
  const monthRef = useRef(month);
  const onChangeRef = useRef(onChange);
  const addMonthsRef = useRef(addMonths);
  const monthHRef = useRef(monthH);
  useEffect(() => {
    monthRef.current = month;
    onChangeRef.current = onChange;
    addMonthsRef.current = addMonths;
    monthHRef.current = monthH;
  });

  // 测量单月高度:固定 6 周(fixedWeeks)+ 定高单元格 -> 三月等高。
  // 取 .hh-cal__month 的 offsetHeight(含 head/weekdays/grid);仅挂载时测一次。
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = slideRef.current;
    if (!el) return;
    const monthEl = el.querySelector<HTMLElement>('.hh-cal__month');
    const h = monthEl ? monthEl.offsetHeight : Math.round(el.offsetHeight / 3);
    if (h > 0) setMonthH(h);
  }, [enabled]);

  // 裁剪窗 scrollTop 兜底归零:用 overflow:clip(非滚动容器)避免 focus-scroll 滚动错位,
  // 旧浏览器(iOS<16)回落到 hidden 时仍是滚动容器,被聚焦 day 按钮移出可视区时浏览器会滚动它。
  useEffect(() => {
    if (!enabled) return;
    const el = clipRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop !== 0) el.scrollTop = 0;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const el = slideRef.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      if (phaseRef.current !== 'idle' || e.touches.length !== 1) return;
      heightRef.current = monthHRef.current;
      if (heightRef.current === 0) return; // 尚未测得高度,放弃本次手势
      const t = e.touches[0];
      startYRef.current = t.clientY;
      startXRef.current = t.clientX;
      lockedRef.current = false;
      phaseRef.current = 'dragging';
      setTransition(false);
    };

    const onMove = (e: TouchEvent) => {
      if (phaseRef.current !== 'dragging') return;
      const t = e.touches[0];
      const dy = t.clientY - startYRef.current;
      const dx = t.clientX - startXRef.current;
      if (!lockedRef.current) {
        if (Math.abs(dy) < 6 && Math.abs(dx) < 6) return; // 等待明显移动
        if (Math.abs(dx) > Math.abs(dy)) {
          // 水平手势:放弃,交还默认(不 preventDefault)
          phaseRef.current = 'idle';
          return;
        }
        lockedRef.current = true;
      }
      e.preventDefault();
      // 轻微橡皮筋:超出单月高度后阻尼
      const max = heightRef.current;
      let o = dy;
      if (o > max) o = max + (o - max) * 0.25;
      else if (o < -max) o = -max + (o + max) * 0.25;
      setOffset(o);
    };

    const finish = (dy: number) => {
      const cur = monthRef.current;
      const height = heightRef.current || monthHRef.current || 0;
      if (height === 0) {
        phaseRef.current = 'idle';
        return;
      }
      phaseRef.current = 'animating';

      if (Math.abs(dy) > threshold && cur) {
        const dir = dy < 0 ? 1 : -1; // 上滑(dy<0)=下月
        const target = dir === 1 ? -height : height; // 整块平移到相邻月完全可见
        // 阶段1:动画把整块平移到相邻月
        setTransition(true);
        setOffset(target);
        window.setTimeout(() => {
          // 阶段2:换月 + 瞬移归 0(无过渡)。相邻月换月后即为新"当月(中间)",
          // 位于同一屏幕位置,故无跳动。
          onChangeRef.current(addMonthsRef.current(cur, dir));
          setTransition(false);
          setOffset(0);
          phaseRef.current = 'idle';
        }, DURATION);
      } else {
        // 回弹
        setTransition(true);
        setOffset(0);
        window.setTimeout(() => {
          phaseRef.current = 'idle';
        }, DURATION);
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (phaseRef.current !== 'dragging') return;
      finish(e.changedTouches[0].clientY - startYRef.current);
    };
    const onCancel = () => {
      if (phaseRef.current !== 'dragging') return;
      finish(0); // 视作未达阈值,回弹
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [enabled, threshold]);

  // 整块平移:基础 -monthH(露出中间月)+ 拖动 offset。
  const translate = -monthH + offset;
  return { clipRef, slideRef, translate, transition, clipHeight: monthH };
}
